import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfWeek,
  format,
  getISOWeek,
  parseISO,
  isValid,
  startOfWeek,
} from "date-fns";
import { PLAN } from "./projectPlanTheme";

export const userInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const PROJECT_COLORS = [
  PLAN.proj1,
  PLAN.proj2,
  PLAN.proj3,
  PLAN.proj4,
  PLAN.proj5,
];

export const eventColorForId = (id) => {
  const n = Number(id) || 0;
  return PROJECT_COLORS[Math.abs(n) % PROJECT_COLORS.length];
};

export const projectColorForId = (id) => eventColorForId(id);

export const avatarColorForId = (id) => eventColorForId(id);

export function parsePlanDayKey(value) {
  if (value == null || value === "") return null;
  return String(value).substring(0, 10);
}

const dayKeyFromResource = (r) => parsePlanDayKey(r?.start_time);
const endDayKeyFromResource = (r) => parsePlanDayKey(r?.end_time) || dayKeyFromResource(r);

function finishResourceSpan(resources, userId) {
  const first = resources[0];
  const startKey = dayKeyFromResource(resources[0]);
  const endKey = endDayKeyFromResource(resources[resources.length - 1]);
  return {
    key: `${userId}-${startKey}-${endKey}-${resources.map((r) => r.id || r.start_time).join("_")}`,
    userId,
    resources,
    startKey,
    endKey,
  };
}

/** One schedule row per employee; bars render only on explicitly assigned days. */
export function groupResourcesByEmployee(resources = []) {
  const byUser = new Map();
  (resources || []).forEach((r) => {
    const uid = r.user_id;
    if (!uid) return;
    if (!byUser.has(uid)) byUser.set(uid, []);
    byUser.get(uid).push(r);
  });

  return Array.from(byUser.entries())
    .map(([userId, list]) => {
      const sorted = [...list].sort((a, b) => {
        const ak = dayKeyFromResource(a) || "";
        const bk = dayKeyFromResource(b) || "";
        return ak.localeCompare(bk);
      });
      const first = sorted[0];
      const user = first.user || { id: userId, displayTitle: `#${userId}` };
      const employmentEnds =
        user.employment_ends ||
        user.employment?.employment_ends ||
        user.employment?.end_date ||
        null;
      const label = user.displayTitle || user.name || `#${userId}`;
      const assignedDayKeys = [
        ...new Set(sorted.map((r) => dayKeyFromResource(r)).filter(Boolean)),
      ].sort();
      let startKey = assignedDayKeys[0] || null;
      let endKey = assignedDayKeys[assignedDayKeys.length - 1] || null;
      const dayCount = assignedDayKeys.length;
      const span = finishResourceSpan(sorted, userId);
      const daySegments = assignedDayKeys.map((dayKey) => ({
        dayKey,
        resource:
          sorted.find((r) => dayKeyFromResource(r) === dayKey) || sorted[0],
      }));
      return {
        key: String(userId),
        userId,
        user,
        employmentEnds,
        label,
        resources: sorted,
        assignedDayKeys,
        daySegments,
        dayCount,
        startKey,
        endKey,
        span,
      };
    })
    .sort((a, b) => (a.label || "").localeCompare(b.label || "", "nb"));
}

export function eventBoundsFromResources(resources = []) {
  let min = null;
  let max = null;
  (resources || []).forEach((r) => {
    const s = dayKeyFromResource(r);
    const e = endDayKeyFromResource(r);
    if (!s) return;
    if (!min || s < min) min = s;
    if (!max || (e || s) > max) max = e || s;
  });
  return { start_date: min, end_date: max };
}

/** Build per-day resource rows for a span (keeps ids for existing days). */
export function buildResourcesForSpan(segment, startDate, endDate) {
  const template = segment?.resources?.[0];
  if (!template || !startDate || !endDate) return [];

  const startH = template.start_time ? new Date(template.start_time).getHours() : 8;
  const endH = template.end_time ? new Date(template.end_time).getHours() : 16;
  const byDay = new Map();
  (segment.resources || []).forEach((r) => {
    const d = dayKeyFromResource(r);
    if (d) byDay.set(d, r);
  });

  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!isValid(start) || !isValid(end)) return [];

  return eachDayOfInterval({ start, end }).map((day) => {
    const dKey = format(day, "yyyy-MM-dd");
    const existing = byDay.get(dKey);
    const startIso = new Date(`${dKey}T${String(startH).padStart(2, "0")}:00:00`).toISOString();
    const endIso = new Date(`${dKey}T${String(endH).padStart(2, "0")}:00:00`).toISOString();
    return {
      id: existing?.id || null,
      user_id: Number(segment.userId),
      start_time: startIso,
      end_time: endIso,
      user: segment.user,
      status: existing?.status || template.status,
    };
  });
}

/** Sykemelding. The person can stay on the plan; these hours are not available capacity. */
export function isSickLeaveRow(row) {
  return row?.absence?.type?.code === "sick_leave";
}

/** Last working day is before the planned period, so they are not an available resource. */
export function employmentEndedBeforePeriod(row, periodStart) {
  const end = String(row?.employment_ends || row?.user?.employment_ends || "").substring(0, 10);
  const start = String(periodStart || "").substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end) || !/^\d{4}-\d{2}-\d{2}$/.test(start)) return false;
  return end < start;
}

/** Per-day absent employees from `/hours/resource_plan` (for schedule grid). */
export function buildAbsencesByDate(resourcePlan) {
  const map = new Map();
  (resourcePlan?.days || []).forEach((day) => {
    const dKey = day?.date;
    if (!dKey) return;
    const entries = [];
    (day.users || []).forEach((row) => {
      if (row.status !== "absent" && !row.absence) return;
      const uid = row.user?.id;
      if (!uid) return;
      const typeTitle =
        row.absence?.type?.title ||
        row.absence?.type?.name ||
        row.absence?.type?.code ||
        null;
      entries.push({
        userId: uid,
        displayTitle: row.user?.displayTitle || row.user?.name || "",
        absenceLabel: typeTitle,
      });
    });
    if (entries.length > 0) {
      entries.sort((a, b) =>
        a.displayTitle.localeCompare(b.displayTitle, "nb")
      );
      map.set(dKey, entries);
    }
  });
  return map;
}

/** Public holidays (røde dager) per day from `/hours/resource_plan`. */
export function buildHolidaysByDate(resourcePlan) {
  const map = new Map();
  (resourcePlan?.days || []).forEach((day) => {
    const dKey = day?.date;
    if (!dKey || !day?.holiday) return;
    map.set(dKey, day.holiday);
  });
  return map;
}

export const getPlanPeriodRange = (dateObj, type) => {
  switch (type) {
    case "week":
      return {
        from: startOfWeek(dateObj, { weekStartsOn: 1 }),
        to: endOfWeek(dateObj, { weekStartsOn: 1 }),
      };
    case "fortnight": {
      const from = startOfWeek(dateObj, { weekStartsOn: 1 });
      return { from, to: addDays(from, 13) };
    }
    case "month":
      return {
        from: new Date(dateObj.getFullYear(), dateObj.getMonth(), 1),
        to: new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0),
      };
    case "quarter": {
      const q = Math.floor(dateObj.getMonth() / 3);
      return {
        from: new Date(dateObj.getFullYear(), q * 3, 1),
        to: new Date(dateObj.getFullYear(), q * 3 + 3, 0),
      };
    }
    case "year":
      return {
        from: new Date(dateObj.getFullYear(), 0, 1),
        to: new Date(dateObj.getFullYear(), 11, 31),
      };
    default:
      return {
        from: new Date(dateObj.getFullYear(), dateObj.getMonth(), 1),
        to: new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0),
      };
  }
};

export const formatPlanPeriodLabel = (from, to, locale) => {
  if (!from || !to) return "";
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
  if (sameMonth) {
    return `${format(from, "d", { locale })} – ${format(to, "d MMM yyyy", { locale })}`;
  }
  return `${format(from, "d. MMM", { locale })} – ${format(to, "d. MMM yyyy", { locale })}`;
};

/** ISO week number for each Monday in a visible day range (for column headers). */
export const weekNumbersForDays = (days = []) => {
  const map = new Map();
  (days || []).forEach((day) => {
    if (day.getDay() !== 1) return;
    map.set(format(day, "yyyy-MM-dd"), getISOWeek(day));
  });
  return map;
};

/** Visible date range + anchor (range start) for week / fortnight / month. */
export const resolvePlanVisibleRange = (anchorDateStr, periodType) => {
  const anchor = new Date(`${anchorDateStr}T12:00:00`);
  if (Number.isNaN(anchor.getTime())) {
    const today = format(new Date(), "yyyy-MM-dd");
    return resolvePlanVisibleRange(today, periodType);
  }
  const { from, to } = getPlanPeriodRange(anchor, periodType);
  return {
    dateFrom: format(from, "yyyy-MM-dd"),
    dateTo: format(to, "yyyy-MM-dd"),
    baseDate: format(from, "yyyy-MM-dd"),
  };
};

/** Navigate prev/next period; returns new anchor (range start). */
export const shiftPlanAnchor = (anchorDateStr, periodType, direction) => {
  const anchor = new Date(`${anchorDateStr}T12:00:00`);
  let next;
  switch (periodType) {
    case "week":
      next = addDays(anchor, direction * 7);
      break;
    case "fortnight":
      next = addDays(anchor, direction * 14);
      break;
    case "month":
      next = addMonths(anchor, direction);
      break;
    default:
      next = addDays(anchor, direction * 7);
  }
  return format(next, "yyyy-MM-dd");
};

/** Anchor for «I dag» — start of current week / fortnight / month. */
export const todayPlanAnchor = (periodType) => {
  const { baseDate } = resolvePlanVisibleRange(format(new Date(), "yyyy-MM-dd"), periodType);
  return baseDate;
};
